import React, { useEffect, useRef } from 'react'
import { Button, Dialog, Icon, Progress, Typography } from '@equinor/eds-core-react'
import { baseFont } from '@/styles'
import styled from 'styled-components'
import { ConfirmDialogType } from '@/core/models/types'
import { close } from '@equinor/eds-icons'

interface Props {
    title?: string
    children: React.ReactElement
    onRejectClick?: () => void
    onConfirmClick?: () => void
    rejectButtonTitle?: string
    confirmButtonTitle?: string
    rejectButtonDisabled?: boolean
    confirmButtonDisabled?: boolean
    hideRejectButton?: boolean
    isBusy?: boolean
    type?: ConfirmDialogType
    onClose?: () => void
    width?: string | number
    maxWidth?: string | number
    dialogStyle?: React.CSSProperties
}

const DEFAULT_REJECT_BUTTON_TITLE: string = 'Cancel'
const DEFAULT_CONFIRM_BUTTON_TITLE: string = 'Ok'

const ConfirmDialog = (props: Props) => {
    const closeButtonRef = useRef<HTMLButtonElement>(null)

    // The underlying native <dialog> autofocuses its first focusable child on open (showModal),
    // which lands on the close button and shows an unwanted focus ring on iOS. Blur it right away.
    useEffect(() => {
        closeButtonRef.current?.blur()
    }, [])

    const dialogStyle: React.CSSProperties = {
        maxWidth: props.maxWidth ?? '300px',
        width: props.width ?? '90vw',
        ...props.dialogStyle,
    }

    return (
        <Dialog open style={dialogStyle}>
            <Dialog.Header>
                <Dialog.Title>{props.title ?? 'Title'}</Dialog.Title>
                {props.onClose && (
                    <CloseButton
                        ref={closeButtonRef}
                        tabIndex={-1}
                        variant="ghost_icon"
                        aria-label="Close modal"
                        onClick={() => props.onClose?.()}
                    >
                        <Icon data={close} size={24} />
                    </CloseButton>
                )}
            </Dialog.Header>

            <Dialog.CustomContent>{props.children}</Dialog.CustomContent>

            <Dialog.Actions style={{ justifySelf: 'end' }}>
                <DialogActionsContainer>
                    {!props.hideRejectButton && (
                        <Button
                            as="button"
                            onClick={() => props.onRejectClick?.()}
                            variant="outlined"
                            disabled={props.rejectButtonDisabled}
                            color={props.type === 'Error' ? 'danger' : 'primary'}
                        >
                            <Label>{props.rejectButtonTitle ?? DEFAULT_REJECT_BUTTON_TITLE}</Label>
                        </Button>
                    )}

                    <Button
                        as="button"
                        onClick={() => props.onConfirmClick?.()}
                        color={props.type === 'Error' ? 'danger' : 'primary'}
                    >
                        {props.isBusy ? (
                            <Progress.Dots />
                        ) : (
                            <StepperStyledActionLabel>
                                {props.confirmButtonTitle ?? DEFAULT_CONFIRM_BUTTON_TITLE}
                            </StepperStyledActionLabel>
                        )}
                    </Button>
                </DialogActionsContainer>
            </Dialog.Actions>
        </Dialog>
    )
}

const StepperStyledActionLabel = styled(Typography)`
    ${baseFont}
    color: white;
`

const Label = styled.span`
    ${baseFont}
`

const CloseButton = styled(Button)`
    height: 32px;
    width: 32px;
`

export const DialogActionsContainer = styled.div`
    display: flex;
    justify-content: flex-end !important;
    gap: 8px;
`

export default ConfirmDialog
